import { z } from 'zod';
import { AuditEventSchema } from './audit';
import { CohorteSchema } from './cohorte';

/**
 * Canales por los que una persona accede al ecosistema:
 * - `web`: tiene fila `usuario` sin `operador` (solo panel).
 * - `movil`: tiene fila `operador` sin `usuario` (solo app).
 * - `ambos`: tiene las dos filas, enlazadas por `usuario.operador_id`.
 * - `ninguno`: caso residual (fila huérfana sin ningún canal).
 */
export const AccesoCanalSchema = z.enum(['web', 'movil', 'ambos', 'ninguno']);
export type AccesoCanal = z.infer<typeof AccesoCanalSchema>;

/**
 * Persona unificada: `usuario` es la canónica (identidad, grupos y credencial
 * primaria) y `operador` es la proyección del canal móvil (cédula, alias).
 *
 * `activo` es booleano en la API (la BD guarda 0/1) y `grupos` son los códigos
 * de `usuario_grupo` (C.3.4: los grupos viven solo en `usuario_grupo`).
 */
export const UsuarioRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  nombre: z.string(),
  apellido: z.string(),
  cedula: z.string().nullable(),
  alias: z.string().nullable(),
  acceso: AccesoCanalSchema,
  grupos: z.array(z.string()),
  activo: z.boolean(),
  ultimo_login_at: z.string().nullable(),
});
export type UsuarioRow = z.infer<typeof UsuarioRowSchema>;

/**
 * Alta de una persona desde el panel.
 *
 * El email es obligatorio y válido: el login web lo usa como identificador
 * canónico (C.3.3). `activar_movil` y `cedula` habilitan además el canal móvil.
 */
export const UsuarioCreateInputSchema = z.object({
  email: z.string().email(),
  nombre: z.string().min(1).max(80),
  apellido: z.string().min(1).max(80),
  password: z.string().min(8).max(200),
  cedula: z.string().min(1).max(40).optional(),
  alias: z.string().min(1).max(120).optional(),
  activar_movil: z.boolean().optional(),
});
export type UsuarioCreateInput = z.infer<typeof UsuarioCreateInputSchema>;

/**
 * Actualización parcial de una persona. La presencia de `password` dispara el
 * hash en el backend; el resto de campos solo se escribe si vienen informed.
 */
export const UsuarioUpdateInputSchema = z.object({
  email: z.string().email().optional(),
  nombre: z.string().min(1).max(80).optional(),
  apellido: z.string().min(1).max(80).optional(),
  cedula: z.string().min(1).max(40).optional(),
  alias: z.string().min(1).max(120).optional(),
  password: z.string().min(8).max(200).optional(),
  activo: z.boolean().optional(),
});
export type UsuarioUpdateInput = z.infer<typeof UsuarioUpdateInputSchema>;

/**
 * Listado de personas unificadas.
 */
export const UsuarioListResponseSchema = z.object({
  data: z.array(UsuarioRowSchema),
  next_cursor: z.string().nullable().optional(),
});
export type UsuarioListResponse = z.infer<typeof UsuarioListResponseSchema>;

/**
 * Cambio de contraseña administrativo (POST /usuarios/:id/password).
 */
export const UsuarioPasswordInputSchema = z.object({
  password: z.string().min(8).max(200),
});
export type UsuarioPasswordInput = z.infer<typeof UsuarioPasswordInputSchema>;

/**
 * Evento de auditoría resuelto para la persona (GET /usuarios/:id/actividad).
 * `actor_nombre` es el nombre ya resuelto (canónico o del canal móvil).
 *
 * Deriva de `AuditEventSchema` (no lo duplica): los campos cuyos datos reales
 * son compatibles se toman tal cual con `pick`, y solo se relajan los dos que
 * guardan identificadores heredados.
 *
 * La laxitud de `actor_id` y `recurso_id` es INTENCIONAL. Datos reales de
 * `audit_log` en Turso `dev` (1389 filas, solo lectura):
 * - `actor_id`: 1270 filas con uuid de 36 chars + 6 con ids de 32 chars sin
 *   guiones (p. ej. `19c15be8d3fbc8ce154d2817274fa9e3`, que es el `usuario.id`
 *   de `support@faugust.com`).
 * - `recurso_id`: 196 filas con uuid de 36 chars + longitudes 8, 9 y 13.
 *
 * `EntityIdSchema` (uuid de 36 chars) NO los cubre, así que exigirlo aquí
 * haría que el endpoint lanzara `ZodError` (500) al parsear esas filas.
 */
export const ActividadRowSchema = AuditEventSchema.pick({
  id: true,
  timestamp: true,
  actor_tipo: true,
  accion: true,
  recurso_tipo: true,
}).extend({
  // `AuditEventSchema` lo declara opcional; aquí la clave siempre llega
  // (con valor o null) porque la fila de `audit_log` siempre tiene la columna.
  recurso_tipo: AuditEventSchema.shape.recurso_tipo.unwrap(),
  // Laxitud INTENCIONAL: hay ids heredados de 32 chars (ver el bloque de arriba).
  actor_id: z.string().nullable(),
  // Laxitud INTENCIONAL: hay longitudes heredadas de 8, 9 y 13 chars.
  recurso_id: z.string().nullable(),
  // Campo añadido por esta feature: el nombre ya resuelto del actor.
  actor_nombre: z.string().nullable(),
});
export type ActividadRow = z.infer<typeof ActividadRowSchema>;

/**
 * Actividad reciente de una persona.
 */
export const UsuarioActividadResponseSchema = z.object({
  data: z.array(ActividadRowSchema),
});
export type UsuarioActividadResponse = z.infer<typeof UsuarioActividadResponseSchema>;

/**
 * Cohortes asignadas a una persona en el canal movil (`cohorte_operador`).
 *
 * El panel las lee del `operador` de la persona: una persona sin acceso movil
 * devuelve `data: []` (no es un error, es el estado real).
 */
export const UsuarioCohortesResponseSchema = z.object({
  data: z.array(CohorteSchema),
});
export type UsuarioCohortesResponse = z.infer<typeof UsuarioCohortesResponseSchema>;
